/**
 * IA Cart Upsell
 * Handles AJAX loading of product recommendations using Shopify's Product Recommendations API
 * Also includes pre-selected products at the end of the slider
 */

if (!customElements.get('ia-cart-upsell')) {

  class IACartUpsell extends HTMLElement {
    constructor() {
      super();
      this.cartProductIds = [];
      this.limit = 10;
      this.intent = 'complementary';
      this.sectionId = null;
      this.slider = null;
      this.slidesContainer = null;
    }

    connectedCallback() {
      this.initializeComponent();
    }

    initializeComponent() {
      // Get configuration from data attributes
      const cartProductIdsStr = this.dataset.cartProductIds;
      this.limit = parseInt(this.dataset.limit) || 10;
      this.intent = this.dataset.intent || 'complementary';
      this.sectionId = this.dataset.sectionId;

      // Parse product IDs from comma-separated strings
      if (cartProductIdsStr) {
        this.cartProductIds = cartProductIdsStr.split(',').map(id => id.trim()).filter(id => id);
      }

      // Find the existing slider and slides container
      this.slider = this.querySelector('ia-slider');
      this.slidesContainer = this.querySelector('.ia-slider__slides');

      if (!this.slidesContainer) {
        console.warn('ia-cart-upsell: No slider slides container found');
        return;
      }

      // Prevent slider from auto-initializing if it has defer-init flag
      // We'll initialize it manually after recommendations load
      
      // Only load recommendations if we have cart products
      // Pre-selected products should already be rendered server-side
      if (this.cartProductIds.length > 0) {
        this.loadRecommendations();
      } else {
        // No cart products to load recommendations from
        // Initialize slider now with just pre-selected products
        this.initializeSlider();
        this.hideIfEmpty();
      }
    }

    async loadRecommendations() {
      try {
        // Use the first cart product ID for recommendations API
        // Shopify's API uses a single product_id parameter
        const primaryProductId = this.cartProductIds[0];
        
        const url = `${window.Shopify.routes.root}recommendations/products?product_id=${primaryProductId}&limit=${this.limit}&intent=${this.intent}&section_id=${this.sectionId}`;
        
        const response = await fetch(url);

        if (!response.ok) {
          this.hideIfEmpty();
          return;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find the section content - try both section ID formats
        // Shopify may use different ID formats in the returned HTML
        let sectionContent = doc.querySelector(`#shopify-section-${this.sectionId}`);
        if (!sectionContent) {
          // Try finding by class or any section with ia-slider
          sectionContent = doc.querySelector('.ia-cart-upsell-section') || 
                          doc.querySelector(`[id^="shopify-section"]`);
        }
        
        if (sectionContent) {
          const sliderElement = sectionContent.querySelector('ia-slider');
          if (sliderElement) {
            const recommendedSlides = sliderElement.querySelectorAll('.ia-slider__slide');
            
            if (recommendedSlides.length > 0) {
              // Filter out products that are already in cart
              // Cart product IDs are passed from Liquid via data attribute
              const filteredSlides = Array.from(recommendedSlides).filter(slide => {
                // Extract product ID from the slide
                const productForm = slide.querySelector('ia-product-form');
                const productId = productForm?.getAttribute('product-id') || 
                                 productForm?.querySelector('form')?.dataset?.productid;
                
                // Exclude if product ID is in cart (compare as strings)
                if (!productId) return false;
                return !this.cartProductIds.includes(String(productId));
              });
              
              if (filteredSlides.length > 0) {
                // Insert filtered recommended slides BEFORE pre-selected products
                // Pre-selected products are already in the DOM, so we prepend recommendations
                const firstSlide = this.slidesContainer.querySelector('.ia-slider__slide');
                filteredSlides.forEach(slide => {
                  const clonedSlide = slide.cloneNode(true);
                  if (firstSlide) {
                    this.slidesContainer.insertBefore(clonedSlide, firstSlide);
                  } else {
                    this.slidesContainer.appendChild(clonedSlide);
                  }
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading cart upsell products:', error);
      } finally {
        // Initialize slider after recommendations are loaded (or failed)
        this.initializeSlider();
        this.hideIfEmpty();
      }
    }

    initializeSlider() {
      // Initialize the slider if it hasn't been initialized yet
      if (this.slider && typeof this.slider.initializeSlideshow === 'function' && !this.slider.slider) {
        this.slider.initializeSlideshow();
      }
    }

    hideIfEmpty() {
      // Hide the component if there are no slides
      const existingSlides = this.slidesContainer?.querySelectorAll('.ia-slider__slide');
      if (!existingSlides || existingSlides.length === 0) {
        this.style.display = 'none';
      }
    }

    // Public method to refresh recommendations
    refresh() {
      // Remove previously loaded recommendations
      if (this.slidesContainer) {
        const preSelectedCount = parseInt(this.dataset.preSelectedCount) || 0;
        const allSlides = this.slidesContainer.querySelectorAll('.ia-slider__slide');
        // Remove slides after the pre-selected ones
        for (let i = allSlides.length - 1; i >= preSelectedCount; i--) {
          allSlides[i].remove();
        }
      }
      this.loadRecommendations();
    }
  }

  customElements.define('ia-cart-upsell', IACartUpsell);
}

